import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { navItems, MAIN_DRAMA_ID } from "./navItems";
import SearchBox from "./SearchBox";
import Footer from "./Footer";
import { getDramaById } from "@/lib/content";

const useActiveCheck = () => {
  const location = useLocation();
  return (itemPath: string) => {
    const [path, query] = itemPath.split("?");
    if (path === "/reviews/fengliu-nvguandian") {
      return location.pathname.startsWith("/reviews");
    }
    if (path === "/category") {
      if (location.pathname !== "/category") return false;
      const itemTab = new URLSearchParams(query || "").get("tab") || "drama";
      const currentTab =
        new URLSearchParams(location.search).get("tab") || "drama";
      return itemTab === currentTab;
    }
    return location.pathname === path;
  };
};

export default function SiteLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const isActive = useActiveCheck();
  const hotDrama = getDramaById(MAIN_DRAMA_ID);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-background-200 bg-background-100 lg:flex">
        <Link
          to="/"
          className="flex items-center gap-3 border-b border-background-200 px-6 py-6"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 font-heading text-xl text-background-50">
            风
          </span>
          <span>
            <span className="block font-heading text-lg font-semibold leading-tight text-foreground-950">
              风流女管家
            </span>
            <span className="block text-xs text-foreground-500">
              影视剧内容站
            </span>
          </span>
        </Link>

        <div className="px-4 py-4">
          <SearchBox />
        </div>

        <nav className="flex-1 overflow-y-auto px-4 pb-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                      active
                        ? "bg-primary-500 font-medium text-background-50"
                        : "text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <span className="flex h-5 w-5 items-center justify-center">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          {hotDrama && (
            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-primary-700">
                <i className="ri-fire-line" />
                本周热播
              </p>
              <Link
                to={`/detail/${hotDrama.id}`}
                className="group flex cursor-pointer gap-3"
              >
                <span className="block h-16 w-12 shrink-0 overflow-hidden rounded bg-background-200">
                  <img
                    src={hotDrama.poster}
                    alt={hotDrama.title}
                    title={`${hotDrama.title} 剧集海报`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                    {hotDrama.title}
                  </span>
                  <span className="mt-1 block text-xs text-foreground-500">
                    {hotDrama.genre}
                  </span>
                </span>
              </Link>
            </div>
          )}
        </nav>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur lg:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2" onClick={closeMenu}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-base text-background-50">
              风
            </span>
            <span className="font-heading text-base font-semibold text-foreground-950">
              风流女管家
            </span>
          </Link>
          <button
            type="button"
            aria-label="打开导航菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
        <div className="px-4 pb-3">
          <SearchBox onNavigate={closeMenu} />
        </div>
        {menuOpen && (
          <nav className="border-t border-background-200 bg-background-50 px-4 py-3">
            <ul className="grid grid-cols-2 gap-2">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <li key={item.label}>
                    <Link
                      to={item.path}
                      onClick={closeMenu}
                      className={`flex items-center gap-2 rounded-md px-3 py-2.5 text-sm transition-colors ${
                        active
                          ? "bg-primary-500 font-medium text-background-50"
                          : "bg-background-100 text-foreground-700"
                      }`}
                    >
                      <span className="flex h-5 w-5 items-center justify-center">
                        <i className={`${item.icon} text-base`} />
                      </span>
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </header>

      {/* Content */}
      <div className="flex min-h-screen flex-col lg:pl-64">
        <main className="flex-1">
          <Outlet />
        </main>
        <Footer />
      </div>
    </div>
  );
}